// src/components/AnadirMascota.jsx
// ======================= AÑADIRMASCOTA — IA OK (remove-background) =======================
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/perfilmascotas.css";
import { Dog, Cat, Camera, X } from "lucide-react";
import Cropper from "react-easy-crop";
import { authFetch } from "../utils/authFetch";

import petfamLogo from "../assets/Petfamp.png";
import petImage from "../assets/Perfil.webp";
import { getBreedImage } from "../config/petBreedImages";

import VetAssistantChat from "./VetAssistantChat";

const FREE_PETS_LIMIT = 2;

// =========================
// ✅ HELPERS PARA SW2
// =========================
function especieContacto(tipoMascotaUI) {
  const t = String(tipoMascotaUI || "").trim().toLowerCase();
  if (t === "perro") return "perro";
  if (t === "gato") return "gato";
  if (t.includes("perr")) return "perro";
  if (t.includes("gat")) return "gato";
  return "perro";
}

function sexoContacto(sexoUI) {
  const s = String(sexoUI || "").trim().toLowerCase();
  if (s.startsWith("h") || s === "f" || s === "hembra") return "HEMBRA";
  if (s.startsWith("m") || s === "m" || s === "macho") return "MACHO";
  return "MACHO";
}

// base64 puro (sin prefijo data:...;base64,)
function base64Puro(b64MaybeDataUrl) {
  const s = String(b64MaybeDataUrl || "").trim();
  if (!s) return "";
  if (s.startsWith("data:")) {
    const comma = s.indexOf(",");
    return comma >= 0 ? s.slice(comma + 1).replace(/\s+/g, "") : "";
  }
  return s.replace(/\s+/g, "");
}

function normalizePetNameUpper(value) {
  return String(value || "").replace(/[0-9]/g, "").toUpperCase();
}

// ✅ Fecha de HOY en formato YYYY-MM-DD, en hora local (no UTC, para que
// no se corra un día). Se usa como tope del campo "Fecha de nacimiento":
// una mascota no puede haber nacido mañana.
function getTodayISO() {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

/**
 * ✅ Convierte cualquier source (blob:, http(s), /ruta) a dataURL
 * Evita CORS/canvas tainted.
 */
async function anySrcToDataURL(src) {
  const s = String(src || "").trim();
  if (!s) throw new Error("Fuente de imagen vacía");

  if (s.startsWith("data:")) return s;

  // blob local
  if (s.startsWith("blob:")) {
    const res = await fetch(s);
    if (!res.ok) throw new Error("No se pudo leer la imagen (blob)");
    const blob = await res.blob();
    return await blobToDataURL(blob);
  }

  // URL remota
  if (s.startsWith("http://") || s.startsWith("https://")) {
    const res = await fetch(s, { mode: "cors" });
    if (!res.ok) throw new Error("No se pudo leer la imagen (url)");
    const blob = await res.blob();
    return await blobToDataURL(blob);
  }

  // ruta relativa/local
  const res = await fetch(s);
  if (!res.ok) throw new Error("No se pudo leer la imagen");
  const blob = await res.blob();
  return await blobToDataURL(blob);
}

// ✅ Quitar fondo por IA (backend -> rembg)
function dataURLToBlob(dataUrl) {
  const parts = String(dataUrl || "").split(",");
  const mime = parts[0]?.match(/data:(.*?);base64/)?.[1] || "image/png";
  const bstr = atob(parts[1] || "");
  let n = bstr.length;
  const u8 = new Uint8Array(n);
  while (n--) u8[n] = bstr.charCodeAt(n);
  return new Blob([u8], { type: mime });
}

async function removeBgViaBackend(dataUrl) {
  const blob = dataURLToBlob(dataUrl);
  const form = new FormData();
  form.append("image", blob, "pet.png"); // ✅ backend espera "image"

  // ✅ MISMO endpoint que PerfilMascota
  const res = await authFetch("/pets/remove-background", {
    method: "POST",
    body: form,
  });

  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(`remove-background falló (${res.status}): ${t}`);
  }

  return await res.json(); // { imageUrl, imageBase64, contentType }
}

export default function AnadirMascota() {
  const location = useLocation();
  const navigate = useNavigate();

  // ==================== USER ID ====================
  const [idUser, setIdUser] = useState(null);
  useEffect(() => {
    const raw = localStorage.getItem("petfamUser");
    if (!raw) return;
    try {
      const u = JSON.parse(raw);
      if (u?.idUser) setIdUser(u.idUser);
    } catch {}
  }, []);

  // ==================== CONTADOR MASCOTAS (FRONT) ====================
  const [petsCount, setPetsCount] = useState(0);
  const [loadingPetsCount, setLoadingPetsCount] = useState(false);

  const refreshPetsCount = async (userId) => {
    if (!userId) return;
    setLoadingPetsCount(true);
    try {
      const res = await authFetch(`/pets/user/${userId}`);
      const json = await res.json().catch(() => null);
      const list = Array.isArray(json?.data) ? json.data : [];
      setPetsCount(list.length);
    } catch {
      // no bloquea
    } finally {
      setLoadingPetsCount(false);
    }
  };

  // ==================== ANIMACIÓN PANEL DERECHO ====================
  const isPanelTransition =
    location.state?.transition === "panel" || location.state?.transition === "up";

  const [entering, setEntering] = useState(isPanelTransition);
  useEffect(() => {
    if (!isPanelTransition) return;
    const t = setTimeout(() => setEntering(false), 420);
    return () => clearTimeout(t);
  }, [isPanelTransition]);

  // ==================== MODAL MASCOTA ====================
  const [mostrarModal, setMostrarModal] = useState(false);
  const [tipoMascota, setTipoMascota] = useState("");

  // ==================== FOTO MASCOTA (CROPPER) ====================
  const [fotoUrl, setFotoUrl] = useState(null); // dataURL final (UI)
  const [fotoBase64, setFotoBase64] = useState(""); // base64 PURO (backend/SW2)
  const [originalSrc, setOriginalSrc] = useState(null);
  const fileRef = useRef(null);

  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState("");

  const [showPhotoTipsModal, setShowPhotoTipsModal] = useState(false);

  // ==================== CAMPOS ====================
  const [nombre, setNombre] = useState("");
  const [fechaNac, setFechaNac] = useState("");
  const [peso, setPeso] = useState("");

  // ✅ Tope del calendario: hoy. Se calcula una sola vez al montar.
  const todayISO = useMemo(() => getTodayISO(), []);

  // ==================== RAZAS ====================
  const DOG_BREEDS = [
    "AIRDALE TERRIER",
    "AKITA",
    "AKITA AMERICANO",
    "BASENJI",
    "BASSET HOUND",
    "BEAGLE",
    "BICHÓN BOLOÑÉS",
    "BICHÓN FRANCÉS",
    "BICHÓN HABANERO",
    "BIG ROPE",
    "BOBTAIL",
    "BORDER COLLIE",
    "BOSTON TERRIER",
    "BÓXER",
    "BOYERO DE BERNA",
    "BRACO DE WEIMAR",
    "BULL TERRIER",
    "BULLDOG AMERICANO",
    "BULLDOG FRANCÉS",
    "BULLDOG INGLÉS",
    "BULLMASTIFF",
    "BULLY AMERICANO",
    "CAIRN TERRIER",
    "CANICHE",
    "CAVALIER KING CHARLES SPANIEL",
    "CHIHUAHUA",
    "CHOW CHOW",
    "COCKER SPANIEL",
    "COLLIE",
    "COLLIE BARBUDO",
    "CORGI GALÉS CARDIGAN",
    "CORGI GALÉS PEMBROKE",
    "CRIOLLO",
    "DÁLMATA",
    "DOGO ARGENTINO",
    "DOGO DE BURDEOS",
    "DOBERMAN",
    "FILA BRASILERO",
    "FLUFFY",
    "FOX TERRIER",
    "FOXHOUND",
    "FRENCHTON",
    "GALGO",
    "GOLDEN RETRIEVER",
    "GRAN DANÉS",
    "GRAN DANÉS ARLEQUIN",
    "GRAN PIRINEO",
    "HUSKY SIBERIANO",
    "JACK RUSSELL TERRIER",
    "KEESHOND",
    "LABRADOR DORADO",
    "LABRADOR NEGRO",
    "LOBERO IRLANDÉS",
    "LHASA APSO",
    "MALAMUTE DE ALASKA",
    "MALTÉS",
    "MANCHESTER TERRIER",
    "MASTÍN ESPAÑOL",
    "MASTÍN ITALIANO",
    "MASTÍN NAPOLITANO",
    "MASTÍN TIBETANO",
    "MESTIZO",
    "PAPILLÓN",
    "PASTOR ALEMÁN",
    "PASTOR AUSTRALIANO",
    "PASTOR BELGA",
    "PASTOR BLANCO SUIZO",
    "PASTOR CATALÁN",
    "PASTOR HOLANDÉS",
    "PASTOR DE SHETLAND",
    "PEQUINÉS",
    "PERRO DE AGUA ESPAÑOL",
    "PERRO SALCHICHA",
    "PINSCHER",
    "PITBULL",
    "POMERANIAN",
    "POMSKY",
    "POODLE",
    "PUG",
    "RHODESIAN RIDGEBACK",
    "ROTTWEILER",
    "SABUESO",
    "SALCHIPUG",
    "SAN BERNARDO",
    "SAMOYEDO",
    "SCHIPPERKE",
    "SCHNAUZER MINIATURA",
    "SHAR PEI",
    "SHIBA INU",
    "SHIH TZU",
    "STAFFORDSHINE TERRIER AMERICANO",
    "TECKEL",
    "TERRANOVA",
    "TERRIER ESCOCÉS",
    "TERRIER GALÉS",
    "TERRIER IRLANDÉS",
    "VIZSLA",
    "WEST HIGHLAND WHITE TERRIER",
    "YORKSHIRE TERRIER",
  ];

  const CAT_BREEDS = [
    "ABISINIO",
    "AMERICANO DE PELO CORTO",
    "AMERICANO DE PELO DURO",
    "ANGORA",
    "AUSTRALIAN MIST",
    "AZUL RUSO",
    "BALINÉS",
    "BENGALÍ",
    "BIRMANO",
    "BOBTAIL AMERICANO",
    "BOBTAIL JAPONÉS",
    "BOMBAY",
    "BOSQUE DE NORUEGA",
    "BRITÁNICO DE PELO LARGO",
    "CALIFORNIA SPANGLED",
    "CHARTREUX",
    "CRIOLLO",
    "CURL AMERICANO",
    "DEVON REX",
    "DON ESFINGE",
    "ESFINGE",
    "EXÓTICO DE PELO CORTO",
    "HIMALAYO",
    "JAVANÉS",
    "KHAO MANEE",
    "LAPERM",
    "MAINE COON",
    "MAU EGIPCIO",
    "MESTIZO",
    "MINSKIN",
    "NEBELUNG",
    "PERSA",
    "RAGDOLL",
    "SAVANNAH",
    "SCOTTISH FOLD",
    "SELKIRK REX",
    "SIAMÉS",
    "SIBERIANO",
    "SINGAPURA",
    "SNOWSHOE",
    "SOKOKE",
    "SOMALÍ",
    "VAN TURCO",
  ];

  const [searchRaza, setSearchRaza] = useState("");
  const [dropdownRaza, setDropdownRaza] = useState(false);
  const [selectedRaza, setSelectedRaza] = useState("");
  const [usarOtraRaza, setUsarOtraRaza] = useState(false);

  const baseBreeds = tipoMascota === "Gato" ? CAT_BREEDS : DOG_BREEDS;
  const filteredRazas = useMemo(
    () => baseBreeds.filter((r) => r.toLowerCase().includes(searchRaza.toLowerCase())),
    [baseBreeds, searchRaza]
  );

  const dropdownRazaRef = useRef(null);

  // ==================== SEXO ====================
  const [dropdownSexo, setDropdownSexo] = useState(false);
  const [selectedSexo, setSelectedSexo] = useState("");
  const sexos = ["MACHO", "HEMBRA"];
  const dropdownSexoRef = useRef(null);

  // ==================== BACKEND ====================
  const [showSuccess, setShowSuccess] = useState(false);

  const [limitModalOpen, setLimitModalOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showVetChat, setShowVetChat] = useState(false);
  const [createdPet, setCreatedPet] = useState(null);

  const [contactoWarning, setContactoWarning] = useState(false);

  // ======================================================
  // ✅ RESET COMPLETO DEL FORM
  // ======================================================
  const resetForm = () => {
    setNombre("");
    setSelectedRaza("");
    setSearchRaza("");
    setUsarOtraRaza(false);
    setSelectedSexo("");
    setFechaNac("");
    setPeso("");

    setErrorMsg("");
    setPhotoError("");
    setDropdownRaza(false);
    setDropdownSexo(false);

    setFotoUrl(null);
    setFotoBase64("");

    if (originalSrc?.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(originalSrc);
      } catch {}
    }
    setOriginalSrc(null);

    setShowCropper(false);

    setShowPhotoTipsModal(false);
    setContactoWarning(false);

    if (fileRef.current) fileRef.current.value = "";
  };

  const handleOpenModal = async (tipo) => {
    setTipoMascota(tipo);
    await refreshPetsCount(idUser);
    setMostrarModal(true);
    resetForm();
  };

  useEffect(() => {
    if (!idUser) return;
    if (mostrarModal || showSuccess) refreshPetsCount(idUser);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostrarModal, showSuccess, idUser]);

  // ==================== CROP ====================
  const onCropComplete = (_, area) => setCroppedAreaPixels(area);

  const onFotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPhotoError("");
    setErrorMsg("");

    if (originalSrc?.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(originalSrc);
      } catch {}
    }

    const src = URL.createObjectURL(file);
    setOriginalSrc(src);
    setShowCropper(true);
  };

  const openPhotoTips = () => {
    if (uploadingPhoto) return;
    setShowPhotoTipsModal(true);
  };

  const triggerFileDirect = () => {
    if (uploadingPhoto) return;
    fileRef.current?.click();
  };

  const confirmPhotoTipsAndOpenGallery = () => {
    setShowPhotoTipsModal(false);
    setTimeout(() => {
      triggerFileDirect();
    }, 120);
  };

  const limpiarFoto = () => {
    setFotoUrl(null);
    setFotoBase64("");
    if (originalSrc?.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(originalSrc);
      } catch {}
    }
    setOriginalSrc(null);
    setPhotoError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const editarFoto = () => {
    setPhotoError("");
    setErrorMsg("");
    setOriginalSrc(originalSrc || fotoUrl);
    setShowCropper(true);
  };

  // ✅ createImage seguro (sin crossOrigin)
  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("No se pudo cargar la imagen para recortar"));
      img.src = url;
    });

  // ✅ recorte robusto: siempre desde dataURL
  async function getCroppedImg(imageSrc, cropPixels, outSize = 800) {
    const safeDataUrl = await anySrcToDataURL(imageSrc);
    const img = await createImage(safeDataUrl);

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = outSize;
    canvas.height = outSize;

    ctx.drawImage(
      img,
      cropPixels.x,
      cropPixels.y,
      cropPixels.width,
      cropPixels.height,
      0,
      0,
      outSize,
      outSize
    );

    return canvas.toDataURL("image/png");
  }

  // ✅ Guardar recorte (igual a PerfilMascota)
  const saveCrop = async () => {
    if (!originalSrc || !croppedAreaPixels) return;

    try {
      setUploadingPhoto(true);
      setPhotoError("");
      setErrorMsg("");

      // 1) recorte local
      const croppedDataUrl = await getCroppedImg(originalSrc, croppedAreaPixels, 800);

      // 2) IA: quitar fondo (backend -> rembg)
      const ai = await removeBgViaBackend(croppedDataUrl);

      // ✅ usamos el BASE64 que devuelve el backend (no leemos imageUrl)
      const ct = ai?.contentType || "image/png";
      const b64 = String(ai?.imageBase64 || "").trim();

      if (!b64) throw new Error("El backend no devolvió imageBase64.");

      setFotoUrl(`data:${ct};base64,${b64}`);
      setFotoBase64(b64);

      setShowCropper(false);
    } catch (e) {
      console.error(e);

      // fallback: si IA falla, dejamos el recorte normal
      try {
        const croppedDataUrl = await getCroppedImg(originalSrc, croppedAreaPixels, 800);
        setFotoUrl(croppedDataUrl);
        setFotoBase64(base64Puro(croppedDataUrl));
        setShowCropper(false);

        setPhotoError("No pudimos quitar el fondo con IA. Guardamos la foto recortada.");
      } catch {
        setPhotoError("No pudimos procesar la foto. Intenta con otra imagen.");
      }
    } finally {
      setUploadingPhoto(false);
    }
  };

  // ==================== CLICK FUERA ====================
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRaza && dropdownRazaRef.current && !dropdownRazaRef.current.contains(e.target)) {
        setDropdownRaza(false);
      }
      if (dropdownSexo && dropdownSexoRef.current && !dropdownSexoRef.current.contains(e.target)) {
        setDropdownSexo(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownRaza, dropdownSexo]);

  const handleCloseVetChat = () => {
    setShowVetChat(false);
    setShowSuccess(true);
    refreshPetsCount(idUser);
  };

  const sendPetToBackend = async (body) => {
    try {
      setLoading(true);
      setErrorMsg("");
      setContactoWarning(false);

      const res = await authFetch("/pets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const json = await res.json().catch(() => null);

      if (res.status === 409) {
        setLimitModalOpen(true);
        return;
      }

      if (res.status === 502 && json?.data) {
        setCreatedPet(json.data);
        setMostrarModal(false);
        setShowVetChat(false);
        setContactoWarning(true);
        setShowSuccess(true);
        refreshPetsCount(idUser);
        return;
      }

      if (res.status === 502) {
        setErrorMsg("No pudimos registrar tu mascota en este momento. Intenta nuevamente.");
        return;
      }

      if (!res.ok || !json || json.status !== 200 || !json.data) {
        setErrorMsg(json?.userMessage || "No se pudo crear la mascota.");
        return;
      }

      setCreatedPet(json.data);
      setMostrarModal(false);
      setShowVetChat(true);
      refreshPetsCount(idUser);
    } catch {
      setErrorMsg("Error al conectar con el servidor.");
    } finally {
      setLoading(false);
    }
  };

  // ==================== SUBMIT ====================
  const onSubmitPet = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!idUser) return setErrorMsg("Debes iniciar sesión nuevamente.");
    if (uploadingPhoto) return setErrorMsg("Espera un momento, estamos procesando la foto...");

    if (!fotoBase64) return setErrorMsg("La foto de tu mascota es obligatoria.");
    if (!tipoMascota) return setErrorMsg("Selecciona Perro o Gato.");
    if (!nombre.trim()) return setErrorMsg("El nombre es obligatorio.");
    if (!selectedRaza.trim()) return setErrorMsg("La raza es obligatoria.");
    if (!selectedSexo) return setErrorMsg("Selecciona el sexo.");
    if (!fechaNac) return setErrorMsg("La fecha de nacimiento es obligatoria.");

    // ✅ Cinturón de seguridad: el atributo max del calendario ya impide
    // elegir un día futuro, pero algunos navegadores dejan escribir la
    // fecha a mano, así que también se valida aquí antes de enviar.
    if (fechaNac > todayISO) {
      return setErrorMsg("La fecha de nacimiento no puede ser una fecha futura.");
    }

    if (!peso || Number(peso) <= 0)
      return setErrorMsg("El peso en kg es obligatorio y debe ser mayor que 0.");

    await refreshPetsCount(idUser);

    // 🔥 En AñadirMascota sí mostramos modal de pago extra (tu UX actual)
    const breedImage = !usarOtraRaza ? getBreedImage(tipoMascota, selectedRaza) : null;

    const bodyBase = {
      idUser,
      name: nombre.trim(),
      species: especieContacto(tipoMascota),
      breed: selectedRaza.trim(),
      sex: sexoContacto(selectedSexo),
      birthDate: fechaNac,
      weightKg: Number(peso),
      cardImageUrl: breedImage,

      // ✅ Enviamos base64 (lo importante)
      profilePhotoBase64: base64Puro(fotoBase64),
      profilePhotoContentType: "image/png",

      // ✅ guardamos dataURL para UI; backend lo guarda en profilePhotoUrl
      profilePhotoUrl: fotoUrl,
    };

    // Si ya llegó al límite de la membresía, no permite crear más mascotas
    if (petsCount >= FREE_PETS_LIMIT) {
      setLimitModalOpen(true);
      return;
    }

    await sendPetToBackend(bodyBase);
  };

  // ==================== SUCCESS: Añadir otra (flujo AñadirMascota) ====================
  const handleAddAnotherFromSuccess = async () => {
    setShowSuccess(false);

    await refreshPetsCount(idUser);

    // si ya llegó al límite, lo mandamos al panel
    if (petsCount >= FREE_PETS_LIMIT) {
      setLimitModalOpen(false);
      navigate("/interfaz", { state: { transition: "panel" } });
      return;
    }

    resetForm();
    setTipoMascota("");
    setMostrarModal(false);
  };

  // ======================================================
  // ================== RENDER =============================
  // ======================================================
  return (
    <div className="wp-shell perfil-shell anadir-shell">
      <button className="anadir-close" onClick={() => navigate("/interfaz", { state: { transition: "panel" } })}>
        <X size={18} />
      </button>

      <main className={`wp-main ${entering ? "is-entering" : ""}`}>
        <header className="wp-header">
          <img src={petfamLogo} alt="Petfam" className="wp-logo" />
        </header>

        <section className="pet-hero">
          <figure className="pet-figure">
            <img src={petImage} alt="Perro y Gato" loading="lazy" />
          </figure>

          <div className="pet-select">
            <h1 className="pet-title">Selecciona tu mascota</h1>

            <div className="pet-card">
              <button className="pet-option" onClick={() => handleOpenModal("Perro")} type="button">
                <span>PERRO</span>
                <Dog size={22} />
              </button>

              <button className="pet-option" onClick={() => handleOpenModal("Gato")} type="button">
                <span>GATO</span>
                <Cat size={22} />
              </button>
            </div>

            {mostrarModal && (
              <p className="required-note" style={{ marginTop: 10 }}>
                {loadingPetsCount ? "Cargando mascotas..." : `Mascotas: ${petsCount}/${FREE_PETS_LIMIT}`}
              </p>
            )}
          </div>
        </section>
      </main>

      {/* ===================== MODAL ===================== */}
      {mostrarModal && (
        <div className="addpet-overlay">
          <div className="modal-contenido">
            <span className="cerrar" onClick={() => setMostrarModal(false)}>
              ✖
            </span>

            <h3>Conozcamos a tu {tipoMascota}</h3>
            <p className="required-note">Los campos marcados con * son obligatorios.</p>

            {/* FOTO */}
            <div className="foto-control">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={onFotoChange}
                className="sr-only-input"
              />

              <button
                className={`foto-circle ${fotoUrl ? "is-loaded" : ""}`}
                onClick={!fotoUrl ? openPhotoTips : undefined}
                type="button"
                disabled={uploadingPhoto}
                aria-disabled={uploadingPhoto}
              >
                {fotoUrl ? <img src={fotoUrl} className="uploader-img" alt="" /> : <Camera className="cam" size={22} />}
              </button>

              <div className="foto-actions">
                {!fotoUrl ? (
                  <button className="link-action" onClick={openPhotoTips} type="button" disabled={uploadingPhoto}>
                    {uploadingPhoto ? "Procesando foto..." : "Sube la foto de tu mascota*"}
                  </button>
                ) : (
                  <>
                    <button className="link-action" onClick={limpiarFoto} type="button" disabled={uploadingPhoto}>
                      Borrar
                    </button>
                    <span className="sep">•</span>
                    <button className="link-action" onClick={openPhotoTips} type="button" disabled={uploadingPhoto}>
                      Reemplazar
                    </button>
                    <span className="sep">•</span>
                    <button className="link-action" onClick={editarFoto} type="button" disabled={uploadingPhoto}>
                      Editar
                    </button>
                  </>
                )}
              </div>

              {uploadingPhoto && (
                <p className="required-note" style={{ marginTop: 8 }}>
                  Procesando foto…
                </p>
              )}
              {photoError && <p className="form-error-msg">{photoError}</p>}
            </div>

            {/* ERROR */}
            {errorMsg && <p className="form-error-msg">{errorMsg}</p>}

            {/* FORM */}
            <form onSubmit={onSubmitPet}>
              <input
                type="text"
                placeholder="Nombre*"
                value={nombre}
                onChange={(e) => setNombre(normalizePetNameUpper(e.target.value))}
                autoComplete="off"
              />

              {/* RAZA */}
              <div className="field-raza">
                <div className="raza-select-shell" onClick={() => setDropdownRaza(!dropdownRaza)}>
                  <span className={selectedRaza ? "raza-display" : "raza-display raza-placeholder"}>
                    {selectedRaza || "Raza*"}
                  </span>
                </div>

                {dropdownRaza && (
                  <div className="raza-dropdown" ref={dropdownRazaRef} onClick={(e) => e.stopPropagation()}>
                    <input
                      className="raza-search"
                      placeholder="Buscar raza..."
                      value={searchRaza}
                      onChange={(e) => {
                        setSearchRaza(e.target.value);
                        setUsarOtraRaza(false);
                      }}
                    />

                    <div className="breed-list">
                      {filteredRazas.length ? (
                        filteredRazas.map((r) => (
                          <button
                            type="button"
                            key={r}
                            className={r === selectedRaza ? "breed-option active" : "breed-option"}
                            onClick={() => {
                              setSelectedRaza(r);
                              setDropdownRaza(false);
                            }}
                          >
                            {r}
                          </button>
                        ))
                      ) : (
                        <div className="no-results">Sin resultados</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Otro */}
                <div className="other-breed">
                  <label className="other-label">
                    <input
                      type="checkbox"
                      checked={usarOtraRaza}
                      onChange={() => {
                        setUsarOtraRaza(!usarOtraRaza);
                        setSelectedRaza("");
                      }}
                    />
                    Otro (escribir raza)
                  </label>

                  {usarOtraRaza && (
                    <input
                      type="text"
                      placeholder="Escribe la raza*"
                      value={selectedRaza}
                      onChange={(e) => setSelectedRaza(e.target.value.toUpperCase())}
                    />
                  )}
                </div>
              </div>

              {/* SEXO */}
              <div className="field-raza">
                <div className="raza-select-shell" onClick={() => setDropdownSexo(!dropdownSexo)}>
                  <span className={selectedSexo ? "raza-display" : "raza-display raza-placeholder"}>
                    {selectedSexo || "Sexo*"}
                  </span>
                </div>

                {dropdownSexo && (
                  <div className="raza-dropdown" ref={dropdownSexoRef} onClick={(e) => e.stopPropagation()}>
                    <div className="breed-list">
                      {sexos.map((s) => (
                        <button
                          type="button"
                          key={s}
                          className={s === selectedSexo ? "breed-option active" : "breed-option"}
                          onClick={() => {
                            setSelectedSexo(s);
                            setDropdownSexo(false);
                          }}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className={`field-date ${fechaNac ? "has-value" : ""}`}>
                {/* ✅ max={todayISO}: el calendario nativo deja en gris todos
                    los días posteriores a hoy, así que no se puede elegir una
                    fecha de nacimiento futura. */}
                <input
                  type="date"
                  className="birthdate-input"
                  value={fechaNac}
                  max={todayISO}
                  onChange={(e) => setFechaNac(e.target.value)}
                  onClick={(e) => e.currentTarget.showPicker?.()}
                  aria-label="Fecha de nacimiento"
                  required
                />
                <span className={`date-placeholder ${fechaNac ? "is-filled" : ""}`}>Fecha de nacimiento*</span>
              </div>

              <input type="number" placeholder="Peso (kg)*" value={peso} onChange={(e) => setPeso(e.target.value)} />

              <button className="btn-add" disabled={loading || uploadingPhoto}>
                {loading ? "Guardando..." : uploadingPhoto ? "Procesando foto..." : "Añadir"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ===================== MODAL RECOMENDACIONES FOTO ===================== */}
      {showPhotoTipsModal && (
        <div className="photo-tips-overlay" onClick={() => setShowPhotoTipsModal(false)}>
          <div className="photo-tips-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="photo-tips-close"
              onClick={() => setShowPhotoTipsModal(false)}
              aria-label="Cerrar recomendaciones"
            >
              <X size={18} />
            </button>

            <h3 className="photo-tips-title">Recomendaciones para la foto</h3>

            <ul className="photo-tips-list">
              <li>Usa una foto en donde tu mascota se vea clara, sola y bien iluminada.</li>
            </ul>

            <div className="photo-tips-actions">
              <button
                type="button"
                className="photo-tips-confirm"
                onClick={confirmPhotoTipsAndOpenGallery}
              >
                Entendido, subir foto
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== CROP ===================== */}
      {showCropper && (
        <div className="cropper-overlay">
          <div className="cropper-modal">
            <div className="cropper-area">
              <Cropper
                image={originalSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            <div className="cropper-controls">
              <label>
                Zoom
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.01"
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                />
              </label>

              <div className="cropper-actions">
                <button className="btn-outline" onClick={() => setShowCropper(false)} type="button" disabled={uploadingPhoto}>
                  Cancelar
                </button>
                <button className="btn-save" onClick={saveCrop} type="button" disabled={uploadingPhoto}>
                  {uploadingPhoto ? "Procesando..." : "Guardar recorte"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===================== MODAL LÍMITE DE MASCOTAS ===================== */}
      {limitModalOpen && (
        <div className="toast-overlay">
          <div className="toast-card">
            <p className="toast-title">Límite de mascotas</p>
            <p className="toast-text">Tu membresía solo permite 2 mascotas por usuario.</p>

            <div className="toast-actions">
              <button
                className="btn-pill"
                type="button"
                onClick={() => setLimitModalOpen(false)}
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== SUCCESS ===================== */}
      {showSuccess && (
        <div className="toast-overlay">
          <div className="toast-card">
            <p className="toast-title">¡Mascota creada exitosamente!</p>

            {contactoWarning && (
              <p className="toast-text" style={{ marginTop: 8 }}>
                Tu mascota fue creada, pero falló la sincronización con <strong>Contacto</strong>.
              </p>
            )}

            <div className="toast-actions">
              <button className="btn-pill" type="button" onClick={handleAddAnotherFromSuccess}>
                Añadir otra
              </button>

              <button
                className="btn-pill"
                type="button"
                onClick={() => navigate("/interfaz", { state: { transition: "panel" } })}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== CHAT ===================== */}
      {showVetChat && createdPet && <VetAssistantChat pet={createdPet} onClose={handleCloseVetChat} />}
    </div>
  );
}